import { createLogger } from '../utils/logger';
import { authedFetch } from './apiClient';

const logger = createLogger('AtaClassificationService');

export interface AtaClassificationRequest {
  tailNumber: string;
  carrier: string;
  aircraftType: string;
  defectDescription: string;
  station: string;
  currentStatus: string;
  inputAtaCode: string;
}

export interface AtaClassificationRecommendation {
  chapter: string;
  section: string;
  ataCodeId: number;
}

export interface AtaClassificationCitation {
  sourceType: string;
}

export interface AtaClassificationResponse {
  recommendation: AtaClassificationRecommendation | null;
  classification: string;
  citations: AtaClassificationCitation[];
  guardrailIntervened: boolean;
}

interface ApiErrorBody {
  message?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return isRecord(value) && (value.message === undefined || typeof value.message === 'string');
}

function isAtaClassificationResponse(value: unknown): value is AtaClassificationResponse {
  return (
    isRecord(value) &&
    (value.recommendation === null || isRecord(value.recommendation)) &&
    typeof value.classification === 'string' &&
    Array.isArray(value.citations) &&
    typeof value.guardrailIntervened === 'boolean'
  );
}

function parseErrorMessage(body: string): string {
  try {
    const parsed: unknown = JSON.parse(body);
    if (isApiErrorBody(parsed) && typeof parsed.message === 'string') {
      return ` — ${parsed.message}`;
    }
  } catch {
    return '';
  }
  return '';
}

/** Requests a single immediate KB-backed ATA recommendation for one Triage defect. */
export async function classifyAtaRecommendation(
  request: AtaClassificationRequest,
): Promise<AtaClassificationResponse> {
  try {
    const response = await authedFetch('/ata-classification', {
      method: 'POST',
      includeContentType: true,
      body: JSON.stringify(request),
    });
    if (!response) throw new Error('ATA classification could not be authenticated');
    if (!response.ok) {
      const errorBody: string = await response.text();
      logger.warn('ATA classification API returned an error', { statusCode: response.status });
      throw new Error(
        `ATA classification API error: ${response.status}${parseErrorMessage(errorBody)}`,
      );
    }
    const responseBody: unknown = await response.json();
    if (!isAtaClassificationResponse(responseBody)) {
      throw new Error('ATA classification API returned an invalid response');
    }
    return responseBody;
  } catch (error: unknown) {
    const message: string = error instanceof Error ? error.message : 'Unknown error';
    logger.warn('ATA classification request failed', { error: message });
    throw error;
  }
}
